import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Listbox } from '@headlessui/react';
import clsx from 'clsx';
import usePopper from '@/hooks/usePopper';

function getOptionLabel(v, options, defaultLabel = '') {
  const opt = options.find(({ value }) => value === v);
  if (opt != null) {
    return opt.label;
  }
  return defaultLabel;
}

const defaultProps = {
  value: null,
  options: [],
  className: null,
  placeholder: 'Seleccionar',
  inputClassName: null,
  optionsClassName: null,
  optionClassName: null,
  optionHighlightedClassName: null,
  optionSelectedClassName: null,
  onChange: () => null,
  onBlur: () => null,
};

const propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      label: PropTypes.node.isRequired,
    })
  ),
  className: PropTypes.string,
  placeholder: PropTypes.string,
  inputClassName: PropTypes.string,
  optionsClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  optionHighlightedClassName: PropTypes.string,
  optionSelectedClassName: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};

export default function ListboxControl({
  options,
  value,
  placeholder,
  className,
  inputClassName,
  optionsClassName,
  optionClassName,
  optionHighlightedClassName,
  optionSelectedClassName,
  onChange,
  onBlur,
}) {
  const [selected, setSelected] = useState(value);

  const modifiers = useMemo(() => [
    {
      name: 'offset',
      options: {
        offset: [0, 10],
      },
    },
    {
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite',
      requires: ['computeStyles'],
      fn: ({ state }) => {
        state.styles.popper.width =
          `${state.rects.reference.width}px`;
      },
      effect({ state }) {
        state.elements.popper.style.width = `${
          state.elements.reference.offsetWidth
        }px`;
      },
    },
  ], []);

  const [trigger, container] = usePopper({
    placement: 'bottom-end',
    strategy: 'fixed',
    modifiers,
  });

  useEffect(() => {
    if (value !== selected) {
      setSelected(value);
    }
  }, [value, selected]);

  const onChangeCallback = useCallback((v) => {
    onChange(v);
  }, [onChange]);

  const onBlurCallback = useCallback(() => {
    onBlur();
  }, [onBlur]);

  let currentLabel = placeholder;
  if (selected !== null) {
    currentLabel = getOptionLabel(selected, options, placeholder);
  }

  return (
    <Listbox
      as="div"
      className={clsx('space-y-1 w-full relative', className)}
      value={selected}
      onChange={onChangeCallback}
    >
      {({ open }) => (
        <Fragment>
          <Listbox.Button className={clsx('flex cursor-pointer relative w-full', inputClassName)} ref={trigger}>
            <div className="w-full truncate text-left">
              {currentLabel}
            </div>
            <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg
                className="h-5 w-5 text-gray-400"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M7 7l3-3 3 3m0 6l-3 3-3-3"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </Listbox.Button>
          <div className={clsx('relative w-full', open === false && 'z-10', open === true && 'z-20')}>
            {open && (
              <Listbox.Options className={clsx('w-full max-h-60 rounded-md py-1 text-base leading-6 shadow-xs overflow-auto focus:outline-none sm:text-sm sm:leading-5', optionsClassName)} ref={container} static>
                {options.map(({ value, label, ...opt }) => (
                  <Listbox.Option key={value} value={value} className="focus:outline-none" {...opt}>
                    {({ selected, active }) => (
                      optionClassName,
                      optionHighlightedClassName,
                      optionSelectedClassName,
                      <div
                        className={clsx(
                          'cursor-pointer focus:outline-none',
                          optionClassName,
                          selected === true && 'pointer-events-none',
                          selected === true && optionSelectedClassName,
                          active === true && optionHighlightedClassName,
                        )}
                      >
                        <div className="w-full truncate">
                          {label}
                        </div>
                      </div>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            )}
          </div>
        </Fragment>
      )}
    </Listbox>
  );
}

ListboxControl.defaultProps = defaultProps;
ListboxControl.propTypes = propTypes;
